import { debounce } from 'quasar'
import { Change } from '@/services/hot/dto/Change'
import { FilterCallbacks } from '@/services/hot/dto/FilterCallbacks'
import Handsontable from 'handsontable'
import 'handsontable/dist/handsontable.full.css'

class Hot {
  hotInstance: any = {}
  viewData: any[] = []
  editData: any[] = []
  changes: Change[] = []
  editMode: boolean = false
  minSpareRows: number = 0
  config: any = {}
  editConfig: any = {}
  onChangeCallbacks: Function[] = []
  filterCallbacks: FilterCallbacks = {}
  beforeChange: Function | null = null
  state = {
    queryString: '',
    filterConditions: {},
  }

  public static createHook = (f: Function) => (changes: any, source: any) => {
    if (!changes || !changes.length || source === 'manual') {
      return () => {
        return false
      }
    }

    return f(changes, source)
  }

  private static findCallback(value: any, query: string): boolean {
    if (value === null) {
      return false
    }

    return (
      value
        .toString()
        .toLowerCase()
        .indexOf(query.toLowerCase()) !== -1
    )
  }

  public init(
    container: Element | string,
    config: any = {},
    editConfig: any = {}
  ) {
    const ht = this
    this.config = config
    this.editConfig = editConfig
    this.hotInstance = createHot(container, { ...this.config })
    this.minSpareRows = config.minSpareRows || 0
    Handsontable.hooks.add(
      'afterChange',
      [
        Hot.createHook((changes: any, source: string) => {
          for (let [rowIndex, prop, oldValue, newValue] of changes) {
            let physicalRowIndex = ht.hotInstance.toPhysicalRow(rowIndex)
            rowIndex = ht.hotInstance.getSourceDataAtRow(physicalRowIndex)[
              'hotRowIndex'
            ]

            // если новая строка, в ней надо проставить hotRowIndex
            // только если таблица не фильтруется, т.к. physicalRowIndex === hotRowIndex только в этом случае
            if (rowIndex === undefined || rowIndex === null) {
              rowIndex = physicalRowIndex
              ht.hotInstance.setDataAtRowProp(
                physicalRowIndex,
                'hotRowIndex',
                physicalRowIndex,
                'manual'
              )
            }

            let changeIndex = ht.changes.findIndex(
              c => c.rowIndex === rowIndex && c.prop === prop
            )

            if (changeIndex === -1) {
              ht.changes.push({ rowIndex, prop, oldValue, newValue })
            } else {
              ht.changes[changeIndex] = { rowIndex, prop, oldValue, newValue }
            }
          }

          this.changes = this.changes.filter(
            ({ oldValue, newValue }) => oldValue !== newValue
          )

          for (let cb of this.onChangeCallbacks) {
            if (typeof cb === 'function') {
              cb(changes, source, ht)
            }
          }
        }),
      ],
      this.hotInstance
    )
    Handsontable.hooks.add(
      'beforeChange',
      [
        Hot.createHook((changes: any, source: string) => {
          if (typeof this.beforeChange === 'function') {
            this.beforeChange(changes, source, ht)
          }
        }),
      ],
      this.hotInstance
    )
  }

  public loadData(data: any[]) {
    let dataWithRowIndexes = data.map((rowData, index) => ({
      ...rowData,
      hotRowIndex: index,
    }))
    this.viewData = dataWithRowIndexes
    this.editData = cloneData(dataWithRowIndexes)
    this.reRender()
  }

  // Переход в режим редактирования
  public setEditMode() {
    this.editMode = true
    this.hotInstance.updateSettings({
      ...this.editConfig,
      readOnly: false,
      minSpareRows: 1,
    })
    this.reRender()
  }

  // Переход в режим просмотра с отменой всех изменений
  public cancelEditMode() {
    this.editMode = false
    this.hotInstance.updateSettings({ ...this.config, readOnly: true })
    this.editData = cloneData(this.viewData)
    this.changes = []
    this.reRender()
  }

  // Переход в режим просмотра с сохранением изменений
  public setViewMode() {
    this.editMode = false
    this.hotInstance.updateSettings({ ...this.config, readOnly: true })
    this.viewData = cloneData(this.editData)
    this.changes = []
    this.reRender()
  }

  public getChanges() {
    return this.changes
  }

  public addAfterChange = (cb: Function) => {
    this.onChangeCallbacks.push(cb)
  }

  public getData() {
    return this.getCurrentData()
  }

  public reRender = () => {
    if (this.state.queryString) {
      this.updateMinSpareRows()
    }

    const currentData = this.getCurrentData()

    this.hotInstance.loadData(currentData)

    if (!this.state.queryString) {
      this.updateMinSpareRows()
    }

    this.highlightSearch()
    this.hotInstance.render()
    this.applySort()
  }

  private updateMinSpareRows = () => {
    let queryString = this.state.queryString
    const config = this.editMode ? { ...this.editConfig } : { ...this.config }
    const minSpareRows = queryString !== '' ? 0 : config.minSpareRows || 0
    this.hotInstance.updateSettings({ minSpareRows })
  }

  private applySort = () => {
    if (
      this.hotInstance.sortColumn !== undefined &&
      this.hotInstance.sortOrder !== undefined
    ) {
      this.hotInstance.sort(
        this.hotInstance.sortColumn,
        this.hotInstance.sortOrder
      )
    }
  }

  private highlightSearch = () => {
    let queryString = this.state.queryString

    if (queryString !== '') {
      const search =
        this.hotInstance.getPlugin('search') || this.hotInstance.search
      search.query(queryString)
    }
  }

  private getCurrentData = () => {
    let queryString = this.state.queryString
    let currentData = this.editMode ? this.editData : this.viewData

    if (Object.keys(this.filterCallbacks).length !== 0) {
      currentData = currentData.filter(rowData =>
        Object.values(this.filterCallbacks).every(filterCallback =>
          filterCallback(rowData)
        )
      )
    }

    if (queryString !== '') {
      currentData = currentData.filter(row =>
        Object.values(row).some(value => Hot.findCallback(value, queryString))
      )
    }

    return currentData
  }

  public setFilter = (name: string, callback: Function) => {
    this.filterCallbacks[name] = callback
    this.reRender()
  }

  public unsetFilter = (name: string) => {
    delete this.filterCallbacks[name]
    this.reRender()
  }

  public search = debounce((queryString: string) => {
    this.state.queryString = queryString.trim()
    this.reRender()
  }, 250)
}

function createHot(container: Element | string, config = {}) {
  if (typeof container === 'string') {
    container = document.querySelector('container') as Element
  }

  const baseConfig = {
    ...{
      data: [],
      stretchH: 'last',
      filters: true,
      search: { queryMethod: onlyExactMatch },
    },
    ...config,
  }

  return new Handsontable(container, baseConfig as Handsontable.GridSettings)
}

function onlyExactMatch(query: string, value: string | null = null) {
  if (value === null) {
    return false
  }

  return (
    value
      .toString()
      .toLowerCase()
      .indexOf(query.toLowerCase()) !== -1
  )
}

function cloneData(data: any[]) {
  return data.map(rowData => ({ ...rowData }))
}

export default Hot
